// src/pages/admin/AdminDashboard.tsx
//
// Each admin role sees a different version of this page:
//
//  admin            — full stats, tier distribution, content health,
//                     fellows summary, quick-action buttons for everything
//
//  committee_chair  — same stats as admin + fellows pipeline focus,
//                     no "Admin Team" quick action
//
//  committee_member — content health (tasks/library draft counts),
//                     announcement shortcut, no user stats
//
//  portal_manager   — content-focused: draft counts, library status,
//                     task publish queue, no user management stats
//
//  regional_rep     — their region's pending task grading queue,
//                     announcement shortcut, fellows nominations,
//                     no system-wide stats

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  BookOpen,
  ClipboardList,
  Star,
  Megaphone,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowRight,
  BarChart2,
  Shield,
  Flame,
  UserCog,
  ChevronRight,
  Upload,
  PlusCircle,
} from "lucide-react";

import { useAppSelector } from "../../app/hooks";
import {
  useGetAdminStatsQuery,
  //useGetAdminMenteeStatsQuery,
} from "../../app/api/adminApi";
//import { useGetAllCyclesQuery } from "../../app/api/programmeApi";
import { useGetUsersQuery } from "../../app/api/adminApi";
import type { UserRole } from "../../app/api/authApi";
import type { AdminDashboardStats } from "../../app/api/adminApi";

/* ─────────────────────────────────────────────────────────────────
   CONSTANTS  (mirrors AdminLayout so colours are consistent)
───────────────────────────────────────────────────────────────── */

export const ROLE_META: Record<
  UserRole,
  { label: string; colour: string; dot: string }
> = {
  admin: {
    label: "Super Admin",
    colour: "bg-red-100    text-red-700",
    dot: "bg-red-500",
  },
  co: {
    label: "Continental Overseer",
    colour: "bg-gold-100    text-gold-700",
    dot: "bg-gold-500",
  },
  cyp: {
    label: "Continental Youth Pastor",
    colour: "bg-teal-100    text-teal-700",
    dot: "bg-teal-500",
  },
  ryp: {
    label: "Regional Youth Pastor",
    colour: "bg-orange-100    text-orange-700",
    dot: "bg-orange-500",
  },
  mentor: {
    label: "Mentor",
    colour: "bg-gray-100   text-gray-600",
    dot: "bg-gray-400",
  },
  committee_chair: {
    label: "Committee Chair",
    colour: "bg-purple-100 text-purple-700",
    dot: "bg-purple-500",
  },
  committee_member: {
    label: "Committee Member",
    colour: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
  },
  portal_manager: {
    label: "Portal Manager",
    colour: "bg-green-100  text-green-700",
    dot: "bg-green-500",
  },
  regional_rep: {
    label: "Regional Rep",
    colour: "bg-amber-100  text-amber-700",
    dot: "bg-amber-500",
  },
  mentee: {
    label: "Mentee",
    colour: "bg-gray-100   text-gray-600",
    dot: "bg-gray-400",
  },
};

const CONTENT_ROLES: UserRole[] = [
  "admin",
  "co",
  "cyp",
  "ryp",
  "mentor",
  "committee_chair",
  "committee_member",
  "portal_manager",
];
const STATS_ROLES: UserRole[] = ["admin", "committee_chair", "co", "cyp", "ryp"];
const USER_MGMT_ROLES: UserRole[] = ["admin", "committee_chair", "co", "cyp", "ryp"];
// const ANNOUNCEMENT_ROLES: UserRole[] = [
//   "admin",
//   "committee_chair",
//   "committee_member",
//   "portal_manager",
//   "regional_rep",
// ];

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVE COMPONENTS
───────────────────────────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-3">
    {children}
  </h2>
);

// A single stat tile — used in every role's view
const StatTile: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  colour: string;
  icon: React.ReactNode;
  href?: string;
  loading?: boolean;
}> = ({ label, value, sub, colour, icon, href, loading }) => {
  const inner = (
    <div
      className={`bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4 h-full
      ${href ? "hover:border-gray-200 hover:shadow-md transition-all duration-150 cursor-pointer" : ""}`}
    >
      <div className="flex items-center gap-1.5 mb-2 text-[11px] text-gray-400 font-medium">
        {icon}
        {label}
      </div>
      {loading ? (
        <Bone className="h-7 w-20 mt-1" />
      ) : (
        <>
          <p className={`text-2xl font-bold ${colour}`}>{value}</p>
          {sub && (
            <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">
              {sub}
            </p>
          )}
        </>
      )}
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
};

// Quick-action card — navigates to a specific admin route
const QuickAction: React.FC<{
  label: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  accent: string; // tailwind bg+text classes for the icon box
  badge?: string;
  badgeColour?: string;
}> = ({ label, description, icon, href, accent, badge, badgeColour }) => (
  <Link
    to={href}
    className="group flex items-start gap-4 bg-white rounded-xl border border-gray-100 shadow-sm p-4
               hover:border-gray-200 hover:shadow-md transition-all duration-150"
  >
    <div
      className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
          {label}
        </p>
        {badge && (
          <span
            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${badgeColour ?? "bg-gray-100 text-gray-500"}`}
          >
            {badge}
          </span>
        )}
      </div>
      <p className="text-[11px] text-gray-400 leading-relaxed truncate">
        {description}
      </p>
    </div>
    <ChevronRight
      size={14}
      className="shrink-0 text-gray-300 group-hover:text-purple-500 mt-0.5 transition-colors"
    />
  </Link>
);

// Horizontal bar showing tier distribution
const TierBar: React.FC<{
  wider: number;
  inner: number;
  fellows: number;
  total: number;
}> = ({ wider, inner, fellows, total }) => {
  if (total === 0)
    return <p className="text-xs text-gray-400">No mentees yet.</p>;
  return (
    <div className="space-y-2">
      {[
        {
          label: "Wider Audience",
          count: wider,
          colour: "bg-green-400",
          text: "text-green-700",
        },
        {
          label: "Inner Circle",
          count: inner,
          colour: "bg-blue-400",
          text: "text-blue-700",
        },
        {
          label: "Legacy Fellows",
          count: fellows,
          colour: "bg-purple-400",
          text: "text-purple-700",
        },
      ].map((t) => (
        <div key={t.label}>
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className={`font-medium ${t.text}`}>{t.label}</span>
            <span className="text-gray-500">
              {t.count}{" "}
              <span className="text-gray-400">
                ({total > 0 ? Math.round((t.count / total) * 100) : 0}%)
              </span>
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${t.colour} transition-all duration-700`}
              style={{ width: `${total > 0 ? (t.count / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ROLE-SPECIFIC SECTIONS
───────────────────────────────────────────────────────────────── */

// ADMIN + COMMITTEE_CHAIR — full stats overview
const FullStatsPanel: React.FC<{
  stats: AdminDashboardStats;
  loading: boolean;
}> = ({ stats, loading }) => (
  <div className="space-y-6">
    {/* Primary stat tiles */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <StatTile
        label="Total members"
        value={loading ? "…" : stats.users.totalMentees}
        colour="text-purple-700"
        icon={<Users size={12} className="text-purple-400" />}
        href="/admin/members/all"
        loading={loading}
      />
      <StatTile
        label="Active members"
        value={loading ? "…" : stats.users.activeMentees}
        colour="text-green-700"
        icon={<CheckCircle2 size={12} className="text-green-400" />}
        href="/admin/members/all"
        loading={loading}
        sub={`${stats.users.totalMentees > 0 ? Math.round((stats.users.activeMentees / stats.users.totalMentees) * 100) : 0}% of total`}
      />
      <StatTile
        label="Fellows selected"
        value={loading ? "…" : stats.fellows.selected}
        colour="text-amber-600"
        icon={<Star size={12} className="text-amber-400" />}
        href="/admin/members/fellows"
        loading={loading}
        sub={`of ${stats.fellows.total} nominated`}
      />
      <StatTile
        label="Admin accounts"
        value={loading ? "…" : stats.users.totalAdmins}
        colour="text-red-600"
        icon={<Shield size={12} className="text-red-400" />}
        href="/admin/team"
        loading={loading}
      />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Tier distribution */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <SectionLabel>Member tier distribution</SectionLabel>
          <Link
            to="/admin/members/all"
            className="text-[11px] font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-0.5"
          >
            View all <ChevronRight size={11} />
          </Link>
        </div>
        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Bone key={i} className="h-6" />
            ))}
          </div>
        ) : (
          <TierBar
            wider={stats.users.tierDistribution.wider_audience}
            inner={stats.users.tierDistribution.inner_circle}
            fellows={stats.users.tierDistribution.legacy_fellows}
            total={stats.users.totalMentees}
          />
        )}
      </div>

      {/* Content health */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <SectionLabel>Content health</SectionLabel>
        {loading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <Bone key={i} className="h-8" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {[
              {
                label: "Tasks",
                published: stats.tasks.published,
                draft: stats.tasks.draft,
                total: stats.tasks.total,
                href: "/admin/programme/tasks",
                colour: "bg-amber-400",
              },
              {
                label: "Library items",
                published: stats.library.published,
                draft: stats.library.draft,
                total: stats.library.total,
                href: "/admin/programme/library",
                colour: "bg-blue-400",
              },
            ].map((c) => (
              <Link
                key={c.label}
                to={c.href}
                className="block hover:bg-gray-50 rounded-lg p-2 -mx-2 transition-colors"
              >
                <div className="flex items-center justify-between text-[11px] mb-1.5">
                  <span className="font-medium text-gray-700">{c.label}</span>
                  <span className="text-gray-400">
                    {c.published} published ·{" "}
                    <span
                      className={
                        c.draft > 0
                          ? "text-amber-600 font-semibold"
                          : "text-gray-400"
                      }
                    >
                      {c.draft} draft
                    </span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${c.colour}`}
                    style={{
                      width: `${c.total > 0 ? (c.published / c.total) * 100 : 0}%`,
                    }}
                  />
                </div>
              </Link>
            ))}
            <div className="flex items-center justify-between text-[11px] text-gray-500 pt-1 border-t border-gray-50">
              <span className="flex items-center gap-1">
                <Megaphone size={10} /> Announcements
              </span>
              <span className="font-medium text-green-700">
                {stats.announcements.published} published
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Active cycle */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <SectionLabel>Programme cycle</SectionLabel>
        {loading ? (
          <Bone className="h-24" />
        ) : stats.programme.activeCycle ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-purple-50 border border-purple-100 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-purple-700 bg-purple-200 px-2 py-0.5 rounded-md uppercase tracking-wide">
                  {stats.programme.activeCycle.quarter}
                </span>
                <span className="text-[11px] text-purple-500 font-medium">
                  Global active
                </span>
              </div>
              <p className="text-sm font-semibold text-purple-900 leading-snug">
                {stats.programme.activeCycle.theme}
              </p>
              {stats.programme.activeCycle.eventDate && (
                <p className="text-[11px] text-purple-400 mt-2 flex items-center gap-1">
                  <Calendar size={10} />
                  {new Date(
                    stats.programme.activeCycle.eventDate,
                  ).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              )}
            </div>
            <Link
              to="/admin/programme/cycles"
              className="text-[11px] font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1"
            >
              Manage cycles <ArrowRight size={11} />
            </Link>
          </div>
        ) : (
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-center">
            <AlertCircle size={18} className="text-amber-400 mx-auto mb-2" />
            <p className="text-xs font-semibold text-amber-700">
              No active quarter
            </p>
            <p className="text-[11px] text-amber-500 mt-1">
              Create and activate a programme cycle to begin.
            </p>
            <Link
              to="/admin/programme/cycles"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 hover:text-amber-800"
            >
              Set up cycles <ArrowRight size={10} />
            </Link>
          </div>
        )}
      </div>
    </div>
  </div>
);

// PORTAL_MANAGER + COMMITTEE_MEMBER — content-focused panel
const ContentPanel: React.FC<{
  stats?: AdminDashboardStats;
  loading: boolean;
}> = ({ stats, loading }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
    <StatTile
      label="Published tasks"
      value={loading ? "…" : (stats?.tasks.published ?? 0)}
      colour="text-green-700"
      icon={<ClipboardList size={12} className="text-green-400" />}
      href="/admin/programme/tasks"
      loading={loading}
      sub={`${stats?.tasks.draft ?? 0} draft`}
    />
    <StatTile
      label="Draft tasks"
      value={loading ? "…" : (stats?.tasks.draft ?? 0)}
      colour={stats?.tasks.draft ? "text-amber-600" : "text-gray-500"}
      icon={<Clock size={12} className="text-amber-400" />}
      href="/admin/programme/tasks"
      loading={loading}
      sub="awaiting publish"
    />
    <StatTile
      label="Library published"
      value={loading ? "…" : (stats?.library.published ?? 0)}
      colour="text-blue-700"
      icon={<BookOpen size={12} className="text-blue-400" />}
      href="/admin/programme/library"
      loading={loading}
      sub={`${stats?.library.draft ?? 0} draft`}
    />
    <StatTile
      label="Announcements"
      value={loading ? "…" : (stats?.announcements.published ?? 0)}
      colour="text-purple-700"
      icon={<Megaphone size={12} className="text-purple-400" />}
      href="/admin/announcements"
      loading={loading}
      sub="published"
    />
  </div>
);

// REGIONAL_REP — region-scoped panel
const RegionalRepPanel: React.FC<{
  user: {
    firstName: string;
    managedRegion?: string | null;
    region?: string | null;
  };
}> = ({ user }) => {
  const region = user.managedRegion ?? user.region ?? "your region";

  // Regional reps can't call /admin/stats (403), so we show
  // contextual info without stats data
  return (
    <div className="bg-white rounded-2xl border border-amber-100 shadow-sm p-5 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <UserCog size={18} className="text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{region}</p>
          <p className="text-[11px] text-gray-400">Your managed region</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
        <Link
          to="/admin/members/fellows"
          className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 hover:border-amber-200 transition-colors group"
        >
          <Star size={15} className="text-amber-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-800 group-hover:text-amber-900">
              Nominations
            </p>
            <p className="text-[11px] text-amber-500">
              Submit & manage fellows nominations
            </p>
          </div>
          <ChevronRight size={12} className="text-amber-400 shrink-0" />
        </Link>
        <Link
          to="/admin/programme/tasks"
          className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-xl px-4 py-3 hover:border-green-200 transition-colors group"
        >
          <ClipboardList size={15} className="text-green-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-green-800 group-hover:text-green-900">
              Task grading
            </p>
            <p className="text-[11px] text-green-500">
              Review and grade pending submissions
            </p>
          </div>
          <ChevronRight size={12} className="text-green-400 shrink-0" />
        </Link>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   QUICK ACTIONS — filtered by role
───────────────────────────────────────────────────────────────── */

interface Action {
  label: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  accent: string;
  badge?: string;
  badgeColour?: string;
  roles: UserRole[];
}

const buildActions = (stats?: AdminDashboardStats): Action[] => [
  {
    label: "Create programme cycle",
    description: "Set up a new Q1–Q4 cycle with event dates and milestones",
    icon: <Calendar size={17} className="text-purple-600" />,
    href: "/admin/programme/cycles",
    accent: "bg-purple-50",
    roles: ["admin", "committee_chair"],
  },
  {
    label: "Add task",
    description: "Create a new Leadership Task for the current quarter",
    icon: <ClipboardList size={17} className="text-amber-600" />,
    href: "/admin/programme/tasks",
    accent: "bg-amber-50",
    badge: stats?.tasks.draft ? `${stats.tasks.draft} draft` : undefined,
    badgeColour: "bg-amber-100 text-amber-700",
    roles: ["admin", "committee_chair", "committee_member", "portal_manager"],
  },
  {
    label: "Upload library content",
    description: "Add a keynote, panel recording, reading list, or resource",
    icon: <Upload size={17} className="text-blue-600" />,
    href: "/admin/programme/library",
    accent: "bg-blue-50",
    badge: stats?.library.draft ? `${stats.library.draft} draft` : undefined,
    badgeColour: "bg-blue-100 text-blue-700",
    roles: ["admin", "committee_chair", "committee_member", "portal_manager"],
  },
  {
    label: "Post announcement",
    description: "Broadcast an update to all YAYA members across Continent 2",
    icon: <Megaphone size={17} className="text-green-600" />,
    href: "/admin/announcements",
    accent: "bg-green-50",
    roles: [
      "admin",
      "committee_chair",
      "committee_member",
      "portal_manager",
      "regional_rep",
    ],
  },
  {
    label: "Manage members",
    description: "View, search, and update member profiles and tiers",
    icon: <Users size={17} className="text-indigo-600" />,
    href: "/admin/members/all",
    accent: "bg-indigo-50",
    roles: ["admin", "committee_chair"],
  },
  {
    label: "Grade submissions",
    description: "Review and score pending task submissions from mentees",
    icon: <CheckCircle2 size={17} className="text-teal-600" />,
    href: "/admin/programme/tasks",
    accent: "bg-teal-50",
    roles: ["admin", "committee_chair", "regional_rep"],
  },
  {
    label: "Fellows nominations",
    description:
      "Nominate mentees from your region for the Legacy Fellows cohort",
    icon: <Star size={17} className="text-amber-600" />,
    href: "/admin/members/fellows",
    accent: "bg-amber-50",
    badge: stats?.fellows.total
      ? `${stats.fellows.total} nominated`
      : undefined,
    badgeColour: "bg-amber-100 text-amber-700",
    roles: ["admin", "committee_chair", "regional_rep"],
  },
  {
    label: "Invite admin",
    description: "Add a new committee member, portal manager, or regional rep",
    icon: <PlusCircle size={17} className="text-red-600" />,
    href: "/admin/team",
    accent: "bg-red-50",
    roles: ["admin"],
  },
  {
    label: "View analytics",
    description: "Submission rates, engagement data, and M&E report inputs",
    icon: <BarChart2 size={17} className="text-violet-600" />,
    href: "/admin/analytics",
    accent: "bg-violet-50",
    roles: ["admin", "committee_chair"],
  },
];

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */

const AdminDashboard: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "admin") as UserRole;

  const canSeeFullStats = STATS_ROLES.includes(role);
  const canSeeContent = CONTENT_ROLES.includes(role);
  const isRegionalRep = role === "regional_rep";

  // Only fetch stats if the role has access — prevents 403s
  const {
    data: statsRes,
    isLoading: statsLoading,
    isError: statsError,
  } = useGetAdminStatsQuery(undefined, {
    skip: !canSeeFullStats && !canSeeContent,
  });

  const stats = statsRes?.data;

  // Recent members — shown to admin + chair only
  const { data: recentMembersRes, isLoading: membersLoading } =
    useGetUsersQuery(
      { role: "mentee", limit: 5, page: 1 },
      { skip: !USER_MGMT_ROLES.includes(role) },
    );
  const recentMembers = recentMembersRes?.data ?? [];

  const roleMeta = ROLE_META[role] ?? ROLE_META["admin"];

  const visibleActions = useMemo(
    () => buildActions(stats).filter((a) => a.roles.includes(role)),
    [stats, role],
  );

  // Greeting based on time of day
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  /* ─────────────────────────────────────────────── */

  return (
    <div className="space-y-7 pb-12">
      {/* HERO GREETING */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600 mb-1">
            GIMLP Admin Panel
          </p>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
            {greeting}, {user.firstName}.
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isRegionalRep
              ? `Managing ${user.region ?? "your region"}.`
              : "Here's an overview of the programme today."}
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${roleMeta.colour}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${roleMeta.dot}`} />
          {roleMeta.label}
        </span>
      </div>

      {/* STATS ERROR — non-blocking, only shown when role has access */}
      {statsError && canSeeFullStats && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-xs text-red-600">
          <AlertCircle size={13} className="shrink-0" />
          Could not load dashboard stats. Check your connection and refresh.
        </div>
      )}

      {/* ══ ROLE-SPECIFIC TOP SECTION ══════════════════════════════ */}

      {/* ADMIN + COMMITTEE_CHAIR — full programme overview */}
      {canSeeFullStats && (
        <FullStatsPanel
          stats={
            stats ?? {
              users: {
                totalMentees: 0,
                activeMentees: 0,
                totalAdmins: 0,
                tierDistribution: {
                  wider_audience: 0,
                  inner_circle: 0,
                  legacy_fellows: 0,
                },
              },
              programme: { activeCycle: null },
              tasks: { total: 0, published: 0, draft: 0 },
              library: { total: 0, published: 0, draft: 0 },
              fellows: { total: 0, selected: 0 },
              announcements: { published: 0 },
            }
          }
          loading={statsLoading}
        />
      )}

      {/* PORTAL_MANAGER + COMMITTEE_MEMBER — content health only */}
      {canSeeContent && !canSeeFullStats && (
        <ContentPanel stats={stats} loading={statsLoading} />
      )}

      {/* REGIONAL_REP — region-scoped panel */}
      {isRegionalRep && (
        <RegionalRepPanel
          user={{
            firstName: user.firstName,
            managedRegion: (user as any).managedRegion ?? null,
            region: user.region ?? null,
          }}
        />
      )}

      {/* ══ QUICK ACTIONS ══════════════════════════════════════════ */}
      <div>
        <SectionLabel>Quick actions</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visibleActions.map((a) => (
            <QuickAction
              key={a.href}
              label={a.label}
              description={a.description}
              icon={a.icon}
              href={a.href}
              accent={a.accent}
              badge={a.badge}
              badgeColour={a.badgeColour}
            />
          ))}
        </div>
      </div>

      {/* ══ RECENT MEMBERS — admin + chair only ════════════════════ */}
      {USER_MGMT_ROLES.includes(role) && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <SectionLabel>Recent registrations</SectionLabel>
            <Link
              to="/admin/members/all"
              className="text-[11px] font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-0.5 transition-colors"
            >
              All members <ChevronRight size={11} />
            </Link>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {membersLoading ? (
              <div className="p-4 space-y-3">
                {[0, 1, 2].map((i) => (
                  <Bone key={i} className="h-10" />
                ))}
              </div>
            ) : recentMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Users size={20} className="text-gray-300 mb-2" />
                <p className="text-xs text-gray-400">No members yet.</p>
                <p className="text-[11px] text-gray-400">
                  Members will appear here once they register.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {recentMembers.map((m) => (
                  <Link
                    key={m.id}
                    to={`/admin/members/all`}
                    className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors group"
                  >
                    {/* Avatar */}
                    <div className="shrink-0 w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700">
                      {m.firstName[0]}
                      {m.lastName[0]}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors truncate">
                        {m.firstName} {m.lastName}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate">
                        {m.email}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full
                        ${
                          m.tier === "legacy_fellows"
                            ? "bg-purple-100 text-purple-700"
                            : m.tier === "inner_circle"
                              ? "bg-blue-100   text-blue-700"
                              : "bg-green-100  text-green-700"
                        }`}
                      >
                        {m.tier === "legacy_fellows"
                          ? "Fellows"
                          : m.tier === "inner_circle"
                            ? "Inner Circle"
                            : "Wider Audience"}
                      </span>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {m.region?.split("—")[1]?.trim() ?? m.region ?? "—"}
                      </p>
                    </div>

                    <ChevronRight
                      size={13}
                      className="shrink-0 text-gray-300 group-hover:text-purple-500 transition-colors"
                    />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ PROGRAMME SETUP PROMPT — shown when no cycles exist ════ */}
      {canSeeFullStats && !statsLoading && !stats?.programme.activeCycle && (
        <div className="relative overflow-hidden rounded-2xl border border-dashed border-purple-200 bg-purple-50 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-purple-100 shrink-0">
            <Flame size={20} className="text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
              Set up the programme to get started
            </h3>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              No active quarter is configured yet. Create and activate a
              programme cycle so mentees can begin their Q1 journey. Use the
              "seed defaults" button to scaffold all four quarters at once.
            </p>
          </div>
          <Link
            to="/admin/programme/cycles"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-xs font-semibold text-white hover:bg-purple-700 active:scale-95 transition-all"
          >
            Set up cycles <ArrowRight size={12} />
          </Link>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
